
import { mapState } from 'vuex';
import { Component, Vue } from 'vue-property-decorator';
import { TRANSLATIONS_FETCH } from '@/store/ActionTypes';
import vBreadcrumbs from '@/components/vBreadcrumbs.vue';
import vAuditLogModal from '@/components/vAuditLogModal.vue';
import vModal from '@/components/vModal.vue';
import vRecipeDeleteConfirmationModal from '@/components/vRecipeDeleteConfirmationModal.vue';
import CaloriePlanner from '@/components/wellness/CaloriePlanner.vue';
import WellnessSidebar from '@/components/wellness/WellnessSidebar.vue';
import type { WellnessItem } from '@/models/wellness/WellnessItem';
import type { WellnessTab } from '@/models/wellness/WellnessTab';
import { LocalizationState } from '@/store/modules/LocalizationModule';
import { ItemCompact } from '@/models/ItemCompact';
import { deleteWellnessItems, getWellnessCalories, listRecipesThatUseIngredient } from '@/services/wellnessApi';

@Component({
  components: {
    vBreadcrumbs,
    vAuditLogModal,
    vModal,
    vRecipeDeleteConfirmationModal,
    CaloriePlanner,
    WellnessSidebar,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
    }),
  },
})
class Wellness extends Vue {
  localizationState: LocalizationState;
  selectedItem: WellnessItem | null = null;
  selectedTab: WellnessTab = 'recipes';
  refreshKey = 0;
  showAuditLogModal = false;
  auditModalTitle = '';
  showDeleteRecipeModal = false;
  showDeleteIngredientModal = false;
  pendingDeleteIds: number[] = [];
  pendingDeleteTab: WellnessTab = 'recipes';
  auditLogItem: ItemCompact = {
    id: 0,
    name: '',
    createdOn: null,
    updatedOn: null,
    createdUserName: '',
    updatedUserName: '',
  };
  ingredientUsageModal = {
    isOpen: false,
    recipes: [] as Array<{ id: number; name: string }>,
  };
  canExportWellnessPdf = false;

  get isWellnessExportDisabled() {
    return !this.canExportWellnessPdf;
  }

  get translations() {
    return this.localizationState.translations;
  }

  translationOrFallback(key: string, fallback: string) {
    const translation = this.translations.get(key);

    return translation === '---' ? fallback : translation;
  }

  triggerPdfExport() {
    const plannerRef = this.$refs.caloriePlanner as any;
    if (plannerRef && typeof plannerRef.exportToPdf === 'function') {
      plannerRef.exportToPdf();
    }
  }

  handleExportAvailabilityChange(canExport: boolean) {
    this.canExportWellnessPdf = canExport;
  }

  created() {
    document.title = 'Wellness Analyzer | MenuSano';
    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: 'pages',
      collections: ['common', 'recipes', 'dishes', 'ingredients'],
    }).catch((error) => {
      if (error.response) {
        this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
      }
      else {
        this.$snotify.error('Network error');
      }
    });
  }

  async handleSelect(payload: { item: WellnessItem; tab: WellnessTab }) {
    this.selectedTab = payload.tab;
    this.selectedItem = {
      ...payload.item,
      calories: undefined,
    };

    try {
      const calories = await getWellnessCalories(payload.tab, Number(payload.item.id));

      if (this.selectedItem?.id !== payload.item.id) {
        return;
      }

      this.selectedItem = {
        ...payload.item,
        calories,
      };
    }
    catch (error: any) {
      if (error.response) {
        this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
      }
      else {
        this.$snotify.error(this.translations.get('network-error'));
      }
    }
  }

  handleTabChange() {
    this.selectedItem = null;
  }

  handleDeleteRequest(payload: { tab: WellnessTab; ids: number[] }) {
    this.pendingDeleteIds = payload.ids;
    this.pendingDeleteTab = payload.tab;

    if (payload.tab === 'customRecipes') {
      this.showDeleteIngredientModal = true;
      return;
    }

    this.showDeleteRecipeModal = true;
  }

  handleOpenStats(payload: { item: WellnessItem; tab: WellnessTab }) {
    this.auditLogItem = {
      id: Number(payload.item.id),
      name: payload.item.name,
      createdOn: payload.item.createdOn || null,
      updatedOn: payload.item.updatedOn || null,
      createdUserName: payload.item.createdUserName || '',
      updatedUserName: payload.item.updatedUserName || '',
      requiresDensityUpdate: payload.item.requiresDensityUpdate || null,
    };

    if (payload.tab === 'recipes') {
      this.auditModalTitle = this.translations.get('recipe_stats');
    }
    else if (payload.tab === 'combinedRecipes') {
      this.auditModalTitle = this.translations.get('dish_stats');
    }
    else {
      this.auditModalTitle = this.translations.get('ingredient_stats');
    }

    this.showAuditLogModal = true;
  }

  async handleOpenUsage(payload: { item: WellnessItem; tab: WellnessTab }) {
    if (payload.tab !== 'customRecipes') {
      return;
    }

    try {
      const response = await listRecipesThatUseIngredient(Number(payload.item.id));
      this.ingredientUsageModal.recipes = response.data.result;
      this.ingredientUsageModal.isOpen = true;
    }
    catch (error: any) {
      if (error.response) {
        this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
      }
      else {
        this.$snotify.error(this.translations.get('network-error'));
      }
    }
  }

  async confirmDelete() {
    try {
      await deleteWellnessItems(this.pendingDeleteTab, this.pendingDeleteIds);
      this.showDeleteRecipeModal = false;
      this.showDeleteIngredientModal = false;

      if (this.selectedItem && this.pendingDeleteIds.includes(Number(this.selectedItem.id))) {
        this.selectedItem = null;
      }

      this.pendingDeleteIds = [];
      this.refreshKey += 1;
      this.$snotify.success(this.translations.get('wellness_deleted_successfully'));
    }
    catch (error: any) {
      if (error.response) {
        this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
      }
      else {
        this.$snotify.error(this.translations.get('network-error'));
      }
    }
  }
}

export default Wellness;
